import { useEffect, useRef, useState } from "react";

import { formatCount, getNiceTicks } from "../utils/chart";

interface BarChartProps {
  values: number[];
  /** Colour of the bars, a css value: the chart has one series and this is its identity. */
  color: string;
  /** What the axis says under bar i, or null to leave it blank. */
  tickLabel: (index: number) => string | null;
  /** What a screen reader says for bar i - the interval and the value, as the popover does. */
  ariaLabel: (index: number) => string;
  /** The content of the popover of bar i. */
  details: (index: number) => React.ReactNode;
  /** Bars drawn paler: the current bucket, which is not over yet. */
  isFaded?: (index: number) => boolean;
}

/**
 * A column chart of one series, with a popover on the bar that was tapped.
 *
 * The bars stand on the zero line, and a negative one - a change, never a count - hangs down
 * from it in the same colour: which side of the line it is on says the sign.
 *
 * HTML and css and not svg: text inside a scaled viewBox grows and shrinks with the chart, and
 * an svg drawn at its real size needs the width measured first. Here the columns are a flex row
 * and the heights are percentages, so the chart fits any width without knowing it.
 *
 * Every column is a button the full height of the plot, not only the painted bar: a thirty bar
 * month on a phone leaves each bar ten pixels wide, and a zero is not painted at all. Being a
 * button also gives it keyboard focus and a label for screen readers for free.
 */
export function BarChart({ values, color, tickLabel, ariaLabel, details, isFaded }: BarChartProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const ticks = getNiceTicks(Math.max(0, ...values), Math.min(0, ...values));
  const bottom = ticks[0]!;
  const top = ticks.at(-1)!;
  const labels = ticks.map(formatCount);

  // the popover goes away on a tap anywhere else, and on escape; listening only while one is
  // open keeps two charts on a screen from closing each other's popovers for no reason
  useEffect(() => {
    if (selected === null) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setSelected(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelected(null);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [selected]);

  // a window of another length is another set of bars, and an index into the old one means
  // nothing there
  useEffect(() => {
    setSelected(null);
  }, [values.length]);

  // where a value is on the plot, from its bottom edge, and how tall a bar of it is
  const share = (value: number) => `${((value - bottom) / (top - bottom)) * 100}%`;
  const span = (value: number) => `${(Math.abs(value) / (top - bottom)) * 100}%`;
  // the popover slides along with its bar: its left edge sits on the first bar, its right edge
  // on the last one, and in between it moves by the same fraction - so it never leaves the
  // chart and nothing has to be measured to know that
  const position = selected === null ? 0 : (selected + 0.5) / values.length;

  return (
    <div className="chart" ref={root}>
      <div className="chart__y" aria-hidden="true">
        {/* the widest label, which below zero may be the bottom one and not the top */}
        <span className="chart__y-sizer">
          {labels.reduce((wide, label) => (label.length > wide.length ? label : wide))}
        </span>
        {ticks.map((tick, index) => (
          <span key={tick} className="chart__y-label" style={{ bottom: share(tick) }}>
            {labels[index]}
          </span>
        ))}
      </div>

      <div className="chart__plot">
        {ticks.map((tick) => (
          <span
            key={tick}
            // the zero line is the floor of the bars only when some of them hang below it
            className={`chart__grid${tick === 0 && bottom < 0 ? " chart__grid--zero" : ""}`}
            style={{ bottom: share(tick) }}
            aria-hidden="true"
          />
        ))}

        <div className="chart__columns">
          {values.map((value, index) => {
            const label = tickLabel(index);
            const classes = ["chart__column"];
            if (isFaded?.(index)) {
              classes.push("chart__column--faded");
            }
            if (selected !== null) {
              classes.push(selected === index ? "chart__column--selected" : "chart__column--dimmed");
            }
            return (
              <button
                // the index is the identity of a bar: the buckets are a fixed run of the window
                key={index}
                type="button"
                className={classes.join(" ")}
                aria-label={ariaLabel(index)}
                aria-pressed={selected === index}
                onClick={() => setSelected((previous) => (previous === index ? null : index))}
              >
                {/* lifted off the bottom of the column to the zero line, or to its own value when
                    it hangs below it: `bottom` of a relative box counts in the column's height */}
                <span
                  className={`chart__bar${value < 0 ? " chart__bar--negative" : ""}`}
                  style={{ bottom: share(Math.min(value, 0)), height: span(value), background: color }}
                />
                {label ? (
                  <span className="chart__x-label" aria-hidden="true">
                    {label}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {selected !== null ? (
          <div
            className="chart__popover"
            role="status"
            style={{
              left: `${position * 100}%`,
              // over the bar, or over the zero line when the bar hangs below it
              bottom: `calc(${share(Math.max(values[selected]!, 0))} + 8px)`,
              transform: `translateX(-${position * 100}%)`,
            }}
          >
            {details(selected)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
